"use client";

import { Card, CircularProgress } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { SERVICE_QUEUE_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetServiceQueues from "@/hooks/requests/service-queue/use-get-service-queues";
import useHasNoPermission from "@/hooks/use-check-permissions";
import usePagination from "@/hooks/use-pagination";
import { ServiceQueueMessages } from "@/messages/service-queue/service-queue.messages";

import { ServiceQueueTable } from "./components/Table";

type Props = {
  messages: ServiceQueueMessages;
};

const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;

function ServiceQueuePage({ messages }: Props) {
  const pagination = usePagination();
  const router = useNavigate();
  const { pageNumber, pageSize } = pagination;
  const { lang } = useLanguage();

  const { data, isLoading, error } = useGetServiceQueues({ pageNumber, pageSize });
  const hasNoPermissionToCreate = useHasNoPermission(SERVICE_QUEUE_PERMISSIONS.CREATE);

  const shouldLoadTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !data && !isLoading && !!error;
  const shouldLoadNoFound = !!data && !isLoading && !error && data.items.length === 0;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.serviceQueueTableTitle}
                </MDTypography>

                  <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => router(ROUTE_URLS.createServiceQueue(lang))}
                  disabled={hasNoPermissionToCreate}
                >
                  {messages.addServiceQueueButton}
                </MDButton>
              </MDBox>

              {!!shouldLoadTable && (
                <ServiceQueueTable pagination={pagination} data={data} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.serviceQueueErrorToLoad}
                  description={error.message}
                  alt={messages.serviceQueueNotFoundAlt}
                />
              )}

              {!!shouldLoadNoFound && (
                <AlertBlock
                  title={messages.serviceQueueNotFoundError}
                  description={messages.serviceQueueNotFoundDescription}
                  alt={messages.serviceQueueNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default ServiceQueuePage;
